import { useEffect, useState } from "react";
import { http, formatApiError } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Bell, Coffee, Glasses, Sun, Moon, Wind, Smartphone } from "lucide-react";
import { toast } from "sonner";

const rows = [
  { key: "wind_down_enabled", label: "Wind down reminder", icon: Wind, tint: "text-violet-300" },
  { key: "caffeine_cutoff_hours_before_sleep", label: "Caffeine cutoff (h before sleep)", icon: Coffee, tint: "text-rose-300", numeric: true },
  { key: "wind_down_before_minutes", label: "Wind down window (min before sleep)", icon: Moon, tint: "text-indigo-300", numeric: true },
  { key: "dark_glasses_enabled", label: "Dark glasses after night shift", icon: Glasses, tint: "text-slate-300" },
  { key: "light_therapy_enabled", label: "Morning bright-light nudge", icon: Sun, tint: "text-amber-300" },
  { key: "wake_alarm_enabled", label: "Adaptive wake alarm", icon: Bell, tint: "text-emerald-300" },
  { key: "push_enabled", label: "Push notifications (mocked)", icon: Smartphone, tint: "text-sky-300" },
];

export default function AlertsReminders() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    http.get("/alerts").then((r) => setCfg(r.data)).catch(() => setCfg({}));
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await http.put("/alerts", cfg);
      toast.success("Alerts updated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to save");
    } finally { setBusy(false); }
  };

  if (!cfg) return null;

  return (
    <section className="space-y-6">
      <div>
        <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Alerts & Reminders</h2>
        <p className="text-slate-400 text-sm mt-1">Get nudged only when your body needs it. Timings adapt to your next shift.</p>
      </div>

      <div className="circa-card p-6 divide-y divide-white/5">
        {rows.map((r) => (
          <div key={r.key} className="py-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-slate-900/60 border border-white/10 flex items-center justify-center">
              <r.icon className={`w-4 h-4 ${r.tint}`} />
            </div>
            <div className="flex-1">
              <Label className="text-slate-100 font-medium">{r.label}</Label>
            </div>
            {r.numeric ? (
              <Input
                type="number"
                min={0}
                max={r.key.includes("hours") ? 24 : 240}
                data-testid={`alerts-input-${r.key}`}
                value={cfg[r.key] ?? 0}
                onChange={(e) => setCfg({ ...cfg, [r.key]: Number(e.target.value) })}
                className="w-24 bg-slate-900/60 border-white/10 text-slate-100 text-right font-mono-ui"
              />
            ) : (
              <Switch
                data-testid={`alerts-toggle-${r.key}`}
                checked={!!cfg[r.key]}
                onCheckedChange={(v) => setCfg({ ...cfg, [r.key]: v })}
              />
            )}
          </div>
        ))}
      </div>

      <div className="flex justify-end">
        <Button
          data-testid="btn-save-alerts"
          onClick={save}
          disabled={busy}
          className="rounded-full bg-indigo-500 hover:bg-indigo-400 text-white glow-indigo"
        >
          {busy ? "Saving…" : "Save preferences"}
        </Button>
      </div>
    </section>
  );
}
